import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import { mq } from "../../styles/canonical/breakpoints";
import {
  kpiFaixaCss,
  kpiTileCss,
  mqTabletApenas,
} from "../../components/ListaCompacta/styles";

// Cor só comunica estado: os tons vêm da paleta canônica (info/warning/
// danger/success/neutral) e nunca de uma cor escolhida pela pessoa.
const TONS = {
  danger: css`
    background: var(--danger-bg);
    border-color: var(--danger-border);
    color: var(--danger-text);
  `,
  info: css`
    background: var(--info-bg);
    border-color: var(--info-border);
    color: var(--info-text);
  `,
  warning: css`
    background: var(--warning-bg);
    border-color: var(--warning-border);
    color: var(--warning-text);
  `,
  success: css`
    background: var(--success-bg);
    border-color: var(--success-border);
    color: var(--success-text);
  `,
  neutral: css`
    background: var(--neutral-bg);
    border-color: var(--neutral-border);
    color: var(--neutral-text);
  `,
};

const EASE_SAIDA = "cubic-bezier(0.23, 1, 0.32, 1)";
const ALVO_TOQUE = "44px";

const foco = css`
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

/**
 * No tablet (dedo), todo alvo cresce para 44 px — no mouse a tela segue
 * densa. Aplicado onde o alvo é menor que isso por padrão.
 */
const alvoNoToque = css`
  @media (pointer: coarse) {
    min-height: ${ALVO_TOQUE};
  }
`;

const pressionavel = css`
  transition: transform 140ms ${EASE_SAIDA}, background-color 140ms ease, border-color 140ms ease;
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

/**
 * Só nesta rota: o <main> do shell perde o teto de 1440 px, o respiro da
 * direita e o de baixo — o mapa usa a largura e a altura inteiras ao lado
 * da sidebar. Não mexe em padding-left/top (sidebar e topbar). Quem quiser o
 * mesmo (a página do setor, por exemplo) marca o contêiner com
 * `data-mapa-largura-total` e monta este componente.
 */
export const LarguraTotalDoMapa = createGlobalStyle`
  main:has([data-mapa-largura-total]) {
    max-width: none;
    margin-left: 0;
    margin-right: 0;
    padding-right: 0;
    padding-bottom: 0;
  }
`;

// ------------------------------------------------------------- página

export const Pagina = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  /* No dedo, os botões do cabeçalho (Organizar, as bandejas) chegam com a
     altura do design system, de 30 px. Aqui eles viram alvo de 44. */
  @media (pointer: coarse) {
    > header button {
      min-height: ${ALVO_TOQUE};
    }
  }
  height: calc(100vh - var(--topbar-height) - var(--safe-area-top, 0px) - var(--mfa-banner-h, 0px));
  height: calc(100dvh - var(--topbar-height) - var(--safe-area-top, 0px) - var(--mfa-banner-h, 0px));
  overflow: hidden;

  @media ${mq.mobile} {
    height: auto;
    overflow: visible;
  }
`;

export const Conteudo = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 24px 16px;

  @media ${mq.mobile} {
    padding: 12px 16px 24px;
  }

  /* Tablet: topo (ações + números) em ~15vh; o resto é mapa. */
  &[data-compacto="sim"] {
    gap: 4px;
    padding: 4px 16px 8px;
  }
`;

export const Barra = styled.div`
  position: relative;
  z-index: 30;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;

  .empurra {
    flex: 1 1 0;
  }

  /* Tablet: título, busca, filtros, bandejas e ações numa linha só. A busca
     é quem encolhe; os botões mantêm o alvo de 44 px do dedo. */
  &[data-compacto="sim"] {
    gap: 4px 8px;

    > label:has(input[type="search"]) {
      flex: 1 1 200px;
      min-width: 150px;
      max-width: 340px;
    }
  }

  /* Celular/tablet (≤1024 px): título numa linha e, embaixo, busca que
     encolhe + botões. Os chips de filtro não aparecem (contador no botão). */
  @media ${mq.tablet} {
    &[data-compacto="sim"] {
      gap: 6px;
    }
    &[data-compacto="sim"] > label:has(input[type="search"]) {
      flex: 1 1 0;
      min-width: 0;
      max-width: none;
    }
    .empurra {
      display: none;
    }
  }
`;

/** Título do tablet: nome da tela e quem está usando, em 44 px de altura. */
export const TituloCompacto = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  max-width: 200px;
  margin-right: 4px;

  h1 {
    margin: 0;
    font-size: var(--text-lg);
    line-height: 1.15;
    color: var(--ink);
    white-space: nowrap;
  }
  span {
    overflow: hidden;
    font-size: var(--text-xs);
    color: var(--ink-3);
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  @media ${mq.tablet} {
    flex: 1 1 100%;
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
    max-width: none;
    margin-right: 0;

    h1 {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 17px;
      line-height: 1.2;
    }
    span {
      flex: 1 1 0;
      min-width: 0;
      font-size: var(--text-min, 12px);
    }
  }
`;

export const CampoBusca = styled.label`
  position: relative;
  flex: 0 1 380px;
  min-width: 200px;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    width: 16px;
    height: 16px;
    color: var(--ink-3);
    pointer-events: none;
  }

  input {
    width: 100%;
    min-height: ${ALVO_TOQUE};
    padding: 0 12px 0 36px;
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: var(--text-base);
    transition: border-color 140ms ease;

    &::placeholder {
      color: var(--ink-3);
    }
    &:hover {
      border-color: var(--border-strong);
    }
    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 1px;
    }
  }

  @media ${mq.mobile} {
    flex: 1 1 100%;
  }
`;

const botaoQuieto = css`
  min-height: ${ALVO_TOQUE};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  white-space: nowrap;
  ${pressionavel}
  ${foco}

  &:hover {
    background: var(--surface-2);
    border-color: var(--border-strong);
  }
  svg {
    width: 16px;
    height: 16px;
    flex: none;
  }
`;

export const BotaoBandeja = styled.button`
  ${botaoQuieto}
  font-weight: var(--weight-medium);
  color: var(--ink-2);

  strong {
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    font-weight: var(--weight-bold);
  }
`;

export const BotaoResultados = styled.button`
  ${botaoQuieto}
  border-color: var(--info-border);
  background: var(--info-bg);
  color: var(--info-text);

  &:hover {
    background: var(--info-bg);
    border-color: var(--info-solid);
  }
`;

/**
 * Avisos do topo flutuando sobre o mapa (tablet): não roubam altura do
 * desenho. Ficam no canto de cima, abaixo dos controles de zoom de ninguém.
 */
export const AvisosSobreOMapa = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 4;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: min(560px, calc(100% - 16px));
  pointer-events: none;

  &:empty {
    display: none;
  }
  > * {
    pointer-events: auto;
    box-shadow: var(--shadow-2);
  }
  > p {
    padding: 6px 12px;
    font-size: var(--text-sm);
  }
`;

export const AvisoOrganizar = styled.p`
  margin: 0;
  padding: 10px 14px;
  border: 1px solid var(--info-border);
  border-radius: var(--radius-lg);
  background: var(--info-bg);
  color: var(--info-text);
  font-size: var(--text-base);
`;

export const AvisoErro = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 10px 14px;
  border: 1px solid var(--danger-border);
  border-radius: var(--radius-lg);
  background: var(--danger-bg);
  color: var(--danger-text);
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
`;

export const Nota = styled.p`
  margin: 0;
  color: var(--ink-3);
  font-size: var(--text-md);
`;

// ------------------------------------------------------------ filtros

export const EnvoltorioFiltro = styled.div`
  position: relative;
`;

export const BotaoFiltro = styled.button`
  ${botaoQuieto}

  &[data-ativo="sim"] {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--ink);
  }
  .contador {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: var(--radius-pill);
    background: var(--accent);
    color: var(--ink-on-accent);
    font-size: var(--text-sm);
    font-weight: var(--weight-bold);
    font-variant-numeric: tabular-nums;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const surgir = keyframes`
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

const surgirSemMovimento = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Dentro do SVG não dá para usar translate/scale (as unidades são as do
// mundo, e o desenho sairia do lugar): a entrada é só opacidade.
const surgirNoMapa = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const entrada = css`
  animation: ${surgir} 160ms ${EASE_SAIDA};
  @media (prefers-reduced-motion: reduce) {
    animation: ${surgirSemMovimento} 120ms ease-out;
  }
`;

export const PainelFiltro = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: min(840px, calc(100vw - 32px));
  max-height: min(72vh, 620px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 16px 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface);
  box-shadow: var(--shadow-3);
  transform-origin: top left;
  ${entrada}
`;

export const TopoFiltro = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  background: var(--surface);
  border-bottom: 1px solid var(--divider);

  strong {
    flex: 1;
    font-size: var(--text-lg);
    font-weight: var(--weight-semibold);
    color: var(--ink);
  }
  .limpar {
    min-height: 36px;
    ${alvoNoToque}
    padding: 0 10px;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--text-md);
    font-weight: var(--weight-semibold);
    cursor: pointer;
    ${foco}
    &:hover:not(:disabled) {
      background: var(--surface-2);
    }
    &:disabled {
      color: var(--ink-4);
      cursor: default;
    }
  }
  .fechar {
    width: 36px;
    height: 36px;
    @media (pointer: coarse) {
      width: 44px;
      height: 44px;
    }
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink-2);
    cursor: pointer;
    ${foco}
    &:hover {
      background: var(--surface-2);
    }
  }
`;

export const GrupoFiltro = styled.section`
  padding: 12px 0 14px;

  & + & {
    border-top: 1px solid var(--divider);
  }
  h3 {
    margin: 0 0 10px;
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    color: var(--ink-3);
    letter-spacing: var(--tracking-wide);
  }
`;

export const GradeFiltros = styled.div`
  display: grid;
  /* auto-FILL: um campo sozinho no grupo ocupa uma coluna, não a linha
     inteira (um seletor de 800 px de largura para "Colaborador" é ruído). */
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px 16px;
`;

export const CampoFiltro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  /* Fichinhas ocupam a faixa inteira e os seletores se alinham na linha
     seguinte. Em duas colunas, "Setor" empurrava "Posto" para uma linha
     sozinho e o grupo virava um L. */
  &[data-largo="sim"] {
    grid-column: 1 / -1;
  }

  .rotulo {
    font-size: var(--text-md);
    font-weight: var(--weight-semibold);
    color: var(--ink-2);
  }
  .alternaveis {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .periodo {
    display: flex;
    gap: 8px;

    label {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: var(--text-sm);
      color: var(--ink-3);
    }
    input {
      min-height: 40px;
      ${alvoNoToque}
      padding: 0 10px;
      border: 1px solid var(--border);
      border-radius: var(--radius-md);
      background: var(--surface);
      color: var(--ink);
      font: inherit;
      font-size: var(--text-md);
      ${foco}
    }
  }
  .seletor {
    position: relative;
  }
`;

export const OpcaoAlternavel = styled.button`
  min-height: 36px;
  ${alvoNoToque}
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  background: var(--surface);
  color: var(--ink-2);
  font: inherit;
  font-size: var(--text-md);
  font-weight: var(--weight-medium);
  cursor: pointer;
  ${pressionavel}
  ${foco}

  &:hover {
    border-color: var(--border-strong);
  }
  &[aria-pressed="true"] {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--ink);
    font-weight: var(--weight-semibold);
  }
`;

export const Segmentado = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  padding: 3px;
  gap: 2px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface-2);

  button {
    min-height: 34px;
    ${alvoNoToque}
    padding: 0 12px;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--text-md);
    font-weight: var(--weight-medium);
    cursor: pointer;
    ${foco}
  }
  button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    font-weight: var(--weight-semibold);
    box-shadow: var(--shadow-1);
  }
`;

export const SeletorFiltro = styled.button`
  width: 100%;
  min-height: 40px;
  ${alvoNoToque}
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
  color: var(--ink-2);
  font: inherit;
  font-size: var(--text-md);
  text-align: left;
  cursor: pointer;
  ${foco}

  .valor {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &[data-ativo="sim"] {
    border-color: var(--accent);
    color: var(--ink);
    font-weight: var(--weight-semibold);
  }
  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
  svg {
    flex: none;
    transition: transform 160ms ${EASE_SAIDA};
  }
`;

export const ListaOpcoes = styled.div`
  margin-top: 6px;
  padding: 6px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface-2);

  input[type="search"] {
    width: 100%;
    min-height: 36px;
    ${alvoNoToque}
    margin-bottom: 6px;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: var(--text-md);
    ${foco}
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 200px;
    overflow-y: auto;
  }
  label {
    min-height: 36px;
    ${alvoNoToque}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 6px;
    border-radius: var(--radius-sm);
    font-size: var(--text-md);
    color: var(--ink);
    cursor: pointer;

    &:hover {
      background: var(--surface);
    }
    span:first-of-type {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--accent);
  }
  .inativo {
    font-size: var(--text-sm);
    color: var(--ink-3);
  }
  .vazio {
    padding: 8px 6px;
    font-size: var(--text-md);
    color: var(--ink-3);
  }
  .limpar {
    margin-top: 4px;
    min-height: 32px;
    padding: 0 6px;
    border: 0;
    background: transparent;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    cursor: pointer;
    ${foco}
  }
`;

export const RodapeFiltro = styled.p`
  position: sticky;
  bottom: 0;
  margin: 0;
  padding: 10px 0 12px;
  border-top: 1px solid var(--divider);
  background: var(--surface);
  color: var(--ink-3);
  font-size: var(--text-md);
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;

  .limpar-todos {
    min-height: 32px;
    ${alvoNoToque}
    padding: 0 8px;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--text-md);
    font-weight: var(--weight-semibold);
    cursor: pointer;
    ${foco}
    &:hover {
      background: var(--surface-2);
    }
  }
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 320px;
  min-height: 32px;
  padding: 0 4px 0 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--surface);
  font-size: var(--text-md);
  color: var(--ink);

  .rotulo {
    color: var(--ink-3);
    white-space: nowrap;
  }
  .valor {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: var(--weight-semibold);
  }
  button {
    width: 26px;
    height: 26px;
    flex: none;
    @media (pointer: coarse) {
      width: 36px;
      height: 36px;
    }
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: var(--radius-pill);
    background: transparent;
    color: var(--ink-3);
    cursor: pointer;
    ${foco}
    &:hover {
      background: var(--surface-2);
      color: var(--ink);
    }
  }
`;

// -------------------------------------------------------------- faixa

export const Faixa = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 4px;
  padding: 2px 6px;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface);

  .empurra {
    flex: 1 1 0;
  }

  /* Tela estreita (tablet deitado): os detalhes saem para a faixa caber em
     uma linha só — cada linha a mais é altura tirada do desenho. */
  @media (max-width: 1220px) {
    padding: 2px 4px;
    .so-largo {
      display: none;
    }
  }

  /* Tablet: sempre UMA linha. O que não couber some pela direita (o "ao
     vivo" é o último) em vez de abrir uma segunda linha sobre o mapa. */
  &[data-compacta="sim"] {
    flex-wrap: nowrap;
    overflow: hidden;
    padding: 0 4px;
    border-radius: var(--radius-lg);

    .so-largo,
    .rotulo-vivo {
      display: none;
    }
    > button,
    > div {
      padding: 0 6px;
    }
    /* O gargalo tem nome de setor, que pode ser longo. */
    strong {
      max-width: 160px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  /* Celular/tablet: faixa rolável de tiles (padrão do redesenho mobile). O
     seletor duplo vence o [data-compacta] do tablet deitado. */
  ${kpiFaixaCss}
  @media ${mq.tablet} {
    &,
    &[data-compacta="sim"] {
      flex-wrap: nowrap;
      overflow-x: auto;
      border: 0;
      border-radius: 0;
      background: transparent;
      padding: 0 16px;
    }
    .empurra {
      display: none;
    }
    > * {
      flex: 0 0 auto;
    }
    &[data-compacta="sim"] strong {
      max-width: 140px;
    }
  }
`;

export const SeparadorFaixa = styled.span`
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: var(--divider);

  @media ${mq.tablet} {
    display: none;
  }
`;

export const ItemFaixa = styled.button`
  min-height: ${ALVO_TOQUE};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;

  @media (max-width: 1220px) {
    padding: 0 7px;
  }
  border: 1px solid transparent;
  border-radius: var(--radius-lg);
  background: transparent;
  color: var(--ink-3);
  font: inherit;
  font-size: var(--text-md);
  white-space: nowrap;
  cursor: pointer;
  ${pressionavel}
  ${foco}

  &:is(div) {
    cursor: default;
  }
  &:is(button):hover {
    background: var(--surface-2);
  }
  svg {
    width: 16px;
    height: 16px;
    flex: none;
  }
  strong {
    color: var(--ink);
    font-size: var(--text-lg);
    font-weight: var(--weight-bold);
    font-variant-numeric: tabular-nums;
  }
  .suave {
    color: var(--ink-3);
  }
  &[data-tom="info"] svg,
  &[data-tom="info"] strong { color: var(--info-text); }
  &[data-tom="warning"] svg,
  &[data-tom="warning"] strong { color: var(--warning-text); }
  &[data-tom="danger"] svg,
  &[data-tom="danger"] strong { color: var(--danger-text); }

  &[aria-pressed="true"] {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--ink-2);
  }

  /* Tile: número em cima (16px), rótulo embaixo; ícone e detalhes somem.
     O kpiTileCss empilha em column-reverse — order 2 põe o número no topo. */
  ${kpiTileCss}
  @media ${mq.tablet} {
    &,
    [data-compacta="sim"] > & {
      min-height: ${ALVO_TOQUE};
      justify-content: center;
      padding: 6px 10px;
      border: 1px solid var(--border);
      background: var(--surface);
    }
    svg,
    .suave {
      display: none;
    }
    strong {
      order: 2;
      font-size: 16px;
      line-height: 1;
    }
    > span {
      order: 1;
      font-size: var(--text-min, 12px);
      line-height: 1.2;
    }
  }
`;

export const StatusVivo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  font-size: var(--text-sm);
  color: var(--ink-3);
  white-space: nowrap;

  .ponto {
    width: 8px;
    height: 8px;
    border-radius: var(--radius-pill);
    background: var(--success-solid);
  }
  &[data-tom="warning"] {
    color: var(--warning-text);
    .ponto { background: var(--warning-solid); }
  }
  button {
    min-height: 32px;
    ${alvoNoToque}
    padding: 0 10px;
    border: 1px solid var(--warning-border);
    border-radius: var(--radius-md);
    background: var(--warning-bg);
    color: var(--warning-text);
    font: inherit;
    font-weight: var(--weight-semibold);
    cursor: pointer;
    ${foco}
  }

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

// ------------------------------------------------------------- palco

/*
 * Cores da planta (tokens da casa). Uma só regra nos dois temas: o que está
 * MAIS EM CIMA é mais claro no claro e mais claro no escuro também — é o que
 * faz a fábrica ter relevo em vez de virar cinco retângulos chapados.
 *
 *   pátio (fora) < zona rebaixada < piso da sala < tampo da bancada
 *
 * Antes o pátio e a zona tinham a MESMA cor no claro (surface-2), e no
 * escuro a zona era mais clara que o piso — a ordem invertia de um tema para
 * o outro e nenhum dos dois lia como planta. Nenhuma cor aqui é decoração:
 * são degraus de elevação; estado continua só nos tons info/warning/danger.
 */
const coresDaPlanta = css`
  --mapa-fora: var(--surface-3);
  --mapa-ponto: var(--border-strong);
  --mapa-piso: var(--surface);
  --mapa-junta: var(--border-subtle);
  --mapa-parede: var(--ink-3);
  --mapa-zona: var(--surface-2);
  --mapa-zona-borda: var(--border-strong);
  --mapa-bancada: var(--surface);
  --mapa-bancada-alto: #ffffff;
  --mapa-bancada-baixo: #eef1f7;
  --mapa-bancada-borda: var(--border-strong);
  --mapa-avatar: var(--surface-3);
  --mapa-sombra: rgba(11, 15, 26, 0.1);
  --mapa-sombra-curta: rgba(11, 15, 26, 0.09);
  --mapa-aresta: rgba(11, 15, 26, 0.16);

  [data-theme="dark"] & {
    --mapa-fora: var(--bg);
    --mapa-ponto: var(--border);
    --mapa-piso: var(--surface-2);
    --mapa-junta: var(--border);
    --mapa-parede: var(--ink-4);
    --mapa-zona: var(--surface);
    --mapa-zona-borda: var(--border-strong);
    --mapa-bancada: var(--surface-3);
    --mapa-bancada-alto: #282e3d;
    --mapa-bancada-baixo: #1a1e29;
    --mapa-bancada-borda: var(--border-strong);
    --mapa-avatar: var(--surface);
    --mapa-sombra: rgba(0, 0, 0, 0.55);
    --mapa-sombra-curta: rgba(0, 0, 0, 0.45);
    --mapa-aresta: rgba(0, 0, 0, 0.7);
  }
`;

// Status → tom, o mesmo de TOM_STATUS_AMBIENTE / TOM_STATUS_PECA do
// vocabulário. Duas listas porque a peça desmembrada tem estados que o ambiente
// não tem (retornada, concluída, cancelada) — o mapa só desenha as que estão
// circulando, mas o CSS cobre todas para nenhuma ficar sem cor.
const TOM_DO_ITEM = [
  { seletor: ".ambiente", status: "aguardando", tom: "neutral" },
  { seletor: ".ambiente", status: "em_producao", tom: "info" },
  { seletor: ".ambiente", status: "pausado", tom: "warning" },
  { seletor: ".peca", status: "aguardando", tom: "neutral" },
  { seletor: ".peca", status: "em_producao", tom: "info" },
  { seletor: ".peca", status: "pausada", tom: "warning" },
  { seletor: ".peca", status: "retornada", tom: "success" },
  { seletor: ".peca", status: "concluida", tom: "success" },
  { seletor: ".peca", status: "cancelada", tom: "neutral" },
];
const TONS_DAS_PILULAS = ["neutral", "info", "warning", "danger"];

export const Palco = styled.div`
  ${coresDaPlanta}
  position: relative;
  flex: 1 1 auto;
  min-height: 360px;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background-color: var(--mapa-fora);
  background-image: radial-gradient(circle, var(--mapa-ponto) 1px, transparent 1.4px);
  overflow: hidden;
  touch-action: none;
  user-select: none;

  svg[role="group"] {
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: grab;
    font-family: var(--font-body);
  }
  &[data-organizando="sim"] svg[role="group"] {
    cursor: default;
  }

  /* ---- corredores do fluxo ----
     Três traços na mesma linha: as paredes do corredor, o piso dele e a
     linha pontilhada do sentido. Um corredor com piso e parede liga as
     salas como uma fábrica; a linha pontilhada sozinha ligava como um
     fluxograma. */
  .corredor-parede {
    stroke: var(--mapa-parede);
    stroke-width: 42;
    stroke-linecap: round;
    fill: none;
    opacity: 0.45;
  }
  .corredor-chao {
    stroke: var(--mapa-piso);
    stroke-width: 36;
    stroke-linecap: round;
    fill: none;
  }
  .corredor {
    stroke: var(--ink-4);
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 0.1 12;
    fill: none;
  }
  .corredor-seta {
    fill: none;
    stroke: var(--ink-4);
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .corredor-rotulo {
    /* ink-2: o rótulo fica sobre o chão do pátio, onde o ink-3 não chega a
       4,5:1 em texto pequeno. */
    fill: var(--ink-2);
    font-weight: 600;
  }
  /* Vão da porta: o piso cobrindo a parede onde o corredor encosta. */
  .porta {
    stroke: var(--mapa-piso);
    stroke-width: 7;
    stroke-linecap: butt;
    pointer-events: none;
  }

  /* ---- sala ---- */
  .sala {
    cursor: pointer;
    transition: opacity 200ms ease;
  }
  .sala[data-apagado="sim"] {
    opacity: 0.35;
  }
  .sala-alvo {
    outline: none;
  }
  /* A sala é um volume pousado no pátio: a sombra é o que separa o piso do
     chão de fora nos dois temas (no escuro a diferença de cinza sozinha não
     dá conta). Cinco filtros no mapa inteiro — barato o bastante para o
     arrasto continuar liso. */
  .piso {
    fill: var(--mapa-piso);
    filter: drop-shadow(0 5px 12px var(--mapa-sombra));
  }
  .piso-textura {
    fill: url(#mapa-producao-piso);
    pointer-events: none;
  }
  .piso-junta {
    fill: none;
    stroke: var(--mapa-junta);
    stroke-width: 1;
  }
  .parede {
    fill: none;
    stroke: var(--mapa-parede);
    stroke-width: 5;
    pointer-events: none;
    transition: stroke 140ms ease;
  }
  .sala:hover .parede {
    stroke: var(--ink-3);
  }
  .sala[data-tom="danger"] .parede {
    stroke: var(--danger-border);
  }
  .sala-alvo:focus-visible ~ .parede {
    stroke: var(--accent);
    stroke-width: 7;
  }
  .sala[data-organizando="sim"] {
    cursor: move;
  }
  .sala[data-organizando="sim"] .parede {
    stroke-dasharray: 14 10;
  }
  /* Sala escolhida no Organizar: a parede cheia e na cor de foco diz "é esta
     que a barra de baixo está editando". */
  .sala[data-selecionada="sim"] .parede {
    stroke: var(--accent);
    stroke-width: 7;
    stroke-dasharray: none;
  }

  .sala-nome {
    fill: var(--ink);
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .sala-numero {
    fill: var(--ink);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
  }
  .sala-legenda,
  .sala-calma {
    fill: var(--ink-3);
    font-weight: 500;
  }
  /* Etapa da planilha que alimenta a sala: o vínculo é o que explica por que
     aquele ambiente está ali, então ele fica à vista, não só no Organizar. */
  .sala-etapa {
    fill: var(--ink-3);
    font-weight: 500;
    letter-spacing: var(--tracking-wide);
  }
  /* No escuro o ink-3 sobre o piso da sala dá 4,43:1 — abaixo do 4,5 que este
     texto pequeno exige. Só no escuro, e só nestas três linhas secundárias; o
     nome (branco) continua sendo o que sobressai. */
  [data-theme="dark"] & {
    .sala-legenda,
    .sala-calma,
    .sala-etapa {
      fill: var(--ink-2);
    }
  }
  /* Pílulas sem contorno: três pílulas contornadas debaixo do nome da sala
     brigavam com ele e entre si. O fundo tonal já basta, e o alerta
     ("atrasados") volta a ganhar do informativo. */
  .pilula rect {
    stroke: none;
  }
  .pilula text {
    font-weight: 600;
  }
  ${TONS_DAS_PILULAS.map((tom) => css`
    .pilula[data-tom="${tom}"] {
      color: var(--${tom}-text);
    }
    .pilula[data-tom="${tom}"] rect {
      fill: var(--${tom}-bg);
    }
    .pilula[data-tom="${tom}"] text {
      fill: var(--${tom}-text);
    }
  `)}
  /* "Sem responsável" não é alerta: fica sem chapa, só texto e ícone, para
     não ter o mesmo peso de "atrasados". */
  .pilula[data-tom="neutral"] {
    color: var(--ink-2);
  }
  .pilula[data-tom="neutral"] rect {
    fill: none;
  }
  .pilula[data-tom="neutral"] text {
    fill: var(--ink-2);
    font-weight: 500;
  }

  /* ---- áreas e bancadas ---- */
  .area {
    transition: opacity 200ms ease;
  }
  .area[data-apagado="sim"],
  .bancada[data-apagado="sim"] {
    opacity: 0.35;
  }
  .area-piso {
    fill: var(--mapa-zona);
    stroke: var(--mapa-zona-borda);
    stroke-width: 1.5;
    stroke-dasharray: 7 5;
  }
  /* A fila também é chão rebaixado: sem preenchimento, um setor com poucos
     ambientes virava um vazio branco que se lê como tela quebrada. */
  .area[data-tipo="fila"] .area-piso {
    fill: var(--mapa-zona);
    opacity: 0.6;
  }
  .area-rotulo {
    fill: var(--ink-2);
    font-weight: 600;
  }
  /* Tampo com luz de cima: um retângulo chapado fica cartão, um tampo com
     leve degradê e aresta embaixo fica móvel visto de cima. */
  .bancada-tampo {
    fill: url(#mapa-producao-tampo);
    stroke: var(--mapa-bancada-borda);
    stroke-width: 1.5;
  }
  .tampo-alto {
    stop-color: var(--mapa-bancada-alto);
  }
  .tampo-baixo {
    stop-color: var(--mapa-bancada-baixo);
  }
  .bancada-aresta {
    stroke: var(--mapa-aresta);
    stroke-width: 3.5;
    stroke-linecap: round;
    fill: none;
  }
  .bancada[data-virtual="sim"] .bancada-tampo {
    fill: none;
    stroke-dasharray: 4 4;
  }
  .bancada[data-virtual="sim"] .bancada-aresta {
    display: none;
  }
  .bancada-rotulo {
    fill: var(--ink-2);
    font-weight: 600;
  }
  .vaga {
    fill: none;
    stroke: var(--border);
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }

  /* ---- pessoas ---- */
  .pessoa {
    cursor: pointer;
    outline: none;
    transition: opacity 200ms ease;
  }
  .pessoa[data-apagado="sim"] {
    opacity: 0.3;
  }
  .pessoa-sombra {
    fill: var(--mapa-sombra-curta);
    pointer-events: none;
  }
  /* A pessoa é uma ficha cheia: de longe, sem as iniciais, a cor do estado
     precisa se ler sozinha (o anel fino virava um risco no chão). */
  .pessoa-anel {
    fill: var(--mapa-avatar);
    stroke: var(--border-strong);
    stroke-width: 2;
  }
  .pessoa[data-situacao="trabalhando"] .pessoa-anel {
    fill: var(--info-bg);
    stroke: var(--info-solid);
    stroke-width: 3;
  }
  .pessoa[data-situacao="pausada"] .pessoa-anel {
    fill: var(--warning-bg);
    stroke: var(--warning-solid);
    stroke-width: 3;
  }
  /* Mapa afastado: sem as iniciais, a ficha fica cheia — um ponto vazado de
     2 px não se lê como pessoa. */
  .pessoa[data-longe="sim"] .pessoa-anel {
    stroke-width: 1.5;
  }
  .pessoa[data-longe="sim"][data-situacao="trabalhando"] .pessoa-anel {
    fill: var(--info-solid);
    stroke: var(--info-solid);
  }
  .pessoa[data-longe="sim"][data-situacao="pausada"] .pessoa-anel {
    fill: var(--warning-solid);
    stroke: var(--warning-solid);
  }
  .pessoa[data-situacao="trabalhando"] .pessoa-iniciais {
    fill: var(--info-text);
  }
  .pessoa[data-situacao="pausada"] .pessoa-iniciais {
    fill: var(--warning-text);
  }
  .pessoa:hover .pessoa-anel {
    stroke-width: 4;
  }
  .pessoa:focus-visible .pessoa-anel {
    stroke: var(--accent);
    stroke-width: 4;
  }
  .pessoa-iniciais {
    fill: var(--ink-2);
    font-weight: 700;
    pointer-events: none;
  }
  .pessoa-selo {
    fill: var(--info-solid);
    stroke: var(--mapa-bancada);
    stroke-width: 1.5;
  }
  .pessoa[data-situacao="pausada"] .pessoa-selo {
    fill: var(--warning-solid);
  }
  .pessoa-nome {
    fill: var(--ink);
    font-weight: 600;
  }
  .pessoa-atividade {
    fill: var(--ink-3);
  }
  .pessoa[data-situacao="trabalhando"] .pessoa-atividade {
    fill: var(--info-text);
  }
  .pessoa[data-situacao="pausada"] .pessoa-atividade {
    fill: var(--warning-text);
  }
  .pulso {
    fill: none;
    stroke: var(--info-solid);
    stroke-width: 2.5;
    pointer-events: none;
  }

  /* ---- itens que andam: ambiente (ficha) e peça desmembrada (pecinha) ----
     A pecinha é menor e leva uma barra da cor do status na lateral: é o que
     diz "pedaço de um ambiente" antes de qualquer rótulo se ler. */
  .ambiente,
  .peca {
    cursor: pointer;
    outline: none;
  }
  .ambiente[data-apagado="sim"],
  .peca[data-apagado="sim"] {
    --item-opacidade: 0.3;
  }
  .item-sombra,
  .item-base,
  .item-corpo,
  .peca-barra,
  .glifo,
  .item-rotulo,
  .item-tarefa,
  .selo-pecas {
    opacity: var(--item-opacidade, 1);
    transition: opacity 200ms ease;
  }
  .item-sombra {
    fill: var(--mapa-sombra-curta);
  }
  /* Base opaca: no escuro os tons são translúcidos e o item sumiria no chão. */
  .item-base {
    fill: var(--mapa-bancada);
  }
  .item-corpo {
    stroke-width: 1.2;
    transition: stroke-width 140ms ease;
  }
  .peca .item-corpo {
    stroke-width: 1;
  }
  ${TOM_DO_ITEM.map(({ seletor, status, tom }) => css`
    ${seletor}[data-status="${status}"] .item-corpo {
      fill: var(--${tom}-bg);
      stroke: var(--${tom}-border);
    }
    ${seletor}[data-status="${status}"] .item-rotulo {
      fill: var(--${tom}-text);
    }
    ${seletor}[data-status="${status}"] .glifo {
      fill: var(--${tom}-solid);
    }
    ${seletor}[data-status="${status}"] .peca-barra {
      fill: var(--${tom}-solid);
    }
    ${seletor}[data-status="${status}"] .item-tarefa {
      fill: var(--${tom}-text);
      stroke: var(--${tom}-bg);
    }
  `)}
  .ambiente[data-atrasada="sim"] .item-corpo,
  .peca[data-atrasada="sim"] .item-corpo {
    stroke: var(--danger-solid);
    stroke-width: 2;
  }
  .peca[data-atrasada="sim"] .peca-barra {
    fill: var(--danger-solid);
  }
  .glifo-vazio {
    fill: none;
    stroke: var(--neutral-solid);
    stroke-width: 1.5;
  }
  .glifo-atraso circle {
    fill: var(--danger-solid);
  }
  .glifo-atraso text {
    fill: var(--ink-on-accent);
    font-weight: 800;
  }
  .item-rotulo {
    font-weight: 600;
    pointer-events: none;
  }
  /* Marca de "tem tarefa aberta": pega o tom do próprio item e leva um anel
     da cor do corpo, para virar um selo na quina em vez de um ponto solto. */
  .item-tarefa {
    fill: var(--ink-3);
    stroke-width: 1.4;
    paint-order: stroke;
  }
  .ambiente:hover .item-corpo,
  .peca:hover .item-corpo {
    stroke-width: 2.5;
  }
  .ambiente:focus-visible .item-corpo,
  .peca:focus-visible .item-corpo {
    stroke: var(--accent);
    stroke-width: 3;
  }
  /* Selo "N peças fora": o ambiente perdeu pedaços de si. Neutro de propósito
     — desmembrar é decisão de produção, não alerta. */
  .selo-pecas rect {
    fill: var(--mapa-bancada);
    stroke: var(--border-strong);
    stroke-width: 1;
  }
  .selo-pecas line {
    stroke: var(--ink-3);
    stroke-width: 1.4;
    stroke-linecap: round;
  }
  .selo-pecas text {
    fill: var(--ink-2);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .mais {
    cursor: pointer;
    outline: none;
  }
  .mais-corpo {
    fill: var(--mapa-bancada);
    stroke: var(--border-strong);
    stroke-width: 1.2;
    stroke-dasharray: 4 3;
  }
  .mais[data-atrasada="sim"] .mais-corpo {
    stroke: var(--danger-solid);
  }
  .mais-texto {
    fill: var(--ink-2);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .mais:hover .mais-corpo {
    stroke-width: 2;
  }
  .mais:focus-visible .mais-corpo {
    stroke: var(--accent);
    stroke-width: 3;
    stroke-dasharray: none;
  }

  /* Setor sem posto nenhum: um lugar vago tracejado, o desenho que já diz
     "falta montar aqui" — em vez de um botão fantasma perdido na quina. */
  .convite {
    cursor: pointer;
    outline: none;
    color: var(--ink-2);
  }
  .convite rect {
    fill: var(--mapa-bancada);
    stroke: var(--border-strong);
    stroke-width: 1.5;
    stroke-dasharray: 6 4;
  }
  .convite:hover rect,
  .convite:focus-visible rect {
    stroke-dasharray: none;
  }
  .convite text {
    fill: var(--ink-2);
    font-weight: 600;
  }
  .convite:hover rect,
  .convite:focus-visible rect {
    stroke: var(--accent);
  }

  /* Placa do cabeçalho fora da sala (sala toda desenhada por dentro): o mesmo
     piso e a mesma parede da sala, para ler como parte dela. */
  .placa {
    fill: var(--surface-2);
    stroke: var(--line-strong, var(--line));
    stroke-width: 1.5;
  }

  /* Alças de tamanho: faixas invisíveis em cada parede e quadrados nos
     cantos. O visível é discreto até a sala ser selecionada — seis salas com
     oito alças cada, todas acesas, viravam um formigueiro. */
  .alca {
    fill: var(--ink-2);
    opacity: 0.55;
  }
  .alcas[data-selecionada="sim"] .alca {
    fill: var(--accent);
    opacity: 1;
  }
  .alca-alvo {
    fill: transparent;
  }
  .alca-alvo:hover + .alca,
  .alcas:hover .alca {
    opacity: 1;
  }
  [data-lado="n"], [data-lado="s"] { cursor: ns-resize; }
  [data-lado="e"], [data-lado="w"] { cursor: ew-resize; }
  [data-lado="nw"], [data-lado="se"] { cursor: nwse-resize; }
  [data-lado="ne"], [data-lado="sw"] { cursor: nesw-resize; }
  /* Alça de redesenhar, no alto da sala: só existe no Organizar. */
  .redesenhar {
    cursor: crosshair;
    outline: none;
    color: var(--ink-on-accent);
  }
  .redesenhar rect {
    fill: var(--accent);
    stroke: none;
  }
  .redesenhar:hover rect,
  .redesenhar:focus-visible rect {
    fill: var(--accent-hover);
  }

  /* Traço cru do dedo: a tinta que o gestor acabou de riscar, antes de a
     correção do traçado endireitar. Fica acima de tudo e não recebe toque. O
     miolo é preenchido enquanto se risca (o SVG fecha o traço sozinho): dá
     para ver a ÁREA que vai virar sala, não só a linha. */
  .tinta {
    fill: var(--accent);
    fill-opacity: 0.12;
    stroke: var(--accent);
    stroke-linecap: round;
    stroke-linejoin: round;
    pointer-events: none;
  }
  /* Contorno já corrigido, enquanto o formulário pede nome e etapa: é o que
     mostra no que o risco torto se transformou. */
  .previa {
    pointer-events: none;
    animation: ${surgirNoMapa} 220ms ${EASE_SAIDA} both;
  }
  .previa-area {
    fill: var(--accent);
    fill-opacity: 0.1;
    stroke: none;
  }
  .previa-contorno {
    fill: none;
    stroke: var(--accent);
    stroke-width: 3;
    stroke-dasharray: 12 7;
    stroke-linejoin: round;
  }
  @media (prefers-reduced-motion: reduce) {
    .previa {
      animation: none;
    }
  }
  &[data-desenhando="sim"] svg[role="group"] {
    cursor: crosshair;
  }
  /* Desenhando, o conteúdo das salas sai de cena: o que importa é o contorno. */
  &[data-desenhando="sim"] .camada-itens,
  &[data-desenhando="sim"] .pessoa,
  &[data-desenhando="sim"] .bancada-rotulo {
    opacity: 0.25;
  }
  /* As alças também somem enquanto se risca: mover e redimensionar não estão
     em jogo agora, e o laranja delas brigava com o laranja da tinta. */
  &[data-desenhando="sim"] .alca,
  &[data-desenhando="sim"] .alca-alvo,
  &[data-desenhando="sim"] .redesenhar {
    display: none;
  }
`;

/*
 * Convite do mapa vazio. Fica sobre o palco, centrado, porque um palco
 * pontilhado sem nenhuma sala se lê como tela quebrada — e a saída (desenhar a
 * primeira sala) tem de estar no mesmo lugar em que falta alguma coisa.
 */
export const ConviteDoMapa = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
  pointer-events: none;

  strong {
    font-size: var(--text-xl);
    color: var(--ink);
    text-wrap: balance;
  }
  p {
    max-width: 46ch;
    margin: 0;
    color: var(--ink-2);
    text-wrap: pretty;
  }
  button {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

/*
 * Barra da sala escolhida no Organizar. Ancorada embaixo à esquerda (os
 * controles de zoom ficam à direita), sobre o palco: as ações da sala aparecem
 * perto da sala, e não num painel que cobre o desenho.
 */
export const BarraDeSelecao = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  max-width: calc(100% - 140px);
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-3);

  .nome {
    font-weight: var(--weight-bold);
    color: var(--ink);
  }
  .etapa {
    padding-right: 4px;
    font-size: var(--text-md);
    color: var(--ink-3);
  }
  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .fechar {
    min-height: ${ALVO_TOQUE};
    padding: 0 10px;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink-3);
    font: inherit;
    cursor: pointer;
    ${foco}
    &:hover {
      background: var(--surface-2);
      color: var(--ink);
    }
  }
`;

export const Controles = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 2;
  display: flex;
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-2);
`;

export const BotaoControle = styled.button`
  width: ${ALVO_TOQUE};
  height: ${ALVO_TOQUE};
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${pressionavel}
  ${foco}

  &:hover {
    background: var(--surface-2);
    color: var(--ink);
  }
`;

// ------------------------------------------------------------ popover

export const CaixaPopover = styled.div`
  position: absolute;
  z-index: 3;
  max-height: calc(100% - 16px);
  overflow-y: auto;
  padding: 14px 16px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface);
  box-shadow: var(--shadow-3);
  color: var(--ink);
  font-size: var(--text-md);
  outline: none;
  cursor: auto;
  user-select: text;
  animation: ${surgir} 160ms ${EASE_SAIDA};

  &[data-origem="top"] { transform-origin: top center; }
  &[data-origem="bottom"] { transform-origin: bottom center; }

  @media (prefers-reduced-motion: reduce) {
    animation: ${surgirSemMovimento} 120ms ease-out;
  }

  h3 {
    margin: 0 28px 2px 0;
    font-size: var(--text-lg);
    font-weight: var(--weight-semibold);
    line-height: 1.25;
    text-wrap: balance;
  }
  .subtitulo {
    margin: 0 0 8px;
    color: var(--ink-3);
  }
  .fechar {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    @media (pointer: coarse) {
      width: 40px;
      height: 40px;
    }
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink-3);
    cursor: pointer;
    ${foco}
    &:hover {
      background: var(--surface-2);
      color: var(--ink);
    }
  }
  dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px 12px;
    margin: 10px 0 0;
  }
  dt {
    color: var(--ink-3);
  }
  dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const ListaPopover = styled.ul`
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  li > button {
    width: 100%;
    min-height: 36px;
    ${alvoNoToque}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 6px;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
    ${foco}
    &:hover {
      background: var(--surface-2);
    }
  }
  .texto {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mais {
    color: var(--ink-3);
    padding-left: 6px;
  }
`;

export const AcoesPopover = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--divider);

  button {
    min-height: 40px;
    ${alvoNoToque}
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-weight: var(--weight-semibold);
    cursor: pointer;
    ${pressionavel}
    ${foco}
    &:hover {
      background: var(--surface-2);
    }
  }
  button.principal {
    border-color: var(--accent);
    background: var(--accent);
    color: var(--ink-on-accent);
    &:hover {
      background: var(--accent-hover);
    }
  }
`;

// ---- Lista de setores (celular) ----

export const ListaSetores = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Celular/tablet: cartões compactos (padrão ListaCompacta) — 2 por linha
     no tablet. */
  @media ${mq.tablet} {
    gap: 6px;
  }
  @media ${mqTabletApenas} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const ItemSetor = styled.button`
  width: 100%;
  min-height: 64px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 4px 12px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;
  ${pressionavel}
  ${foco}

  .nome {
    font-weight: var(--weight-bold);
    font-size: var(--text-lg);
  }
  .numero {
    grid-row: span 3;
    font-size: var(--text-3xl);
    font-weight: var(--weight-bold);
    font-variant-numeric: tabular-nums;
  }
  .etapa {
    font-size: var(--text-md);
    color: var(--ink-3);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .alertas {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .calmo {
    font-size: var(--text-md);
    color: var(--ink-3);
  }
  &[data-tom="danger"] {
    border-color: var(--danger-border);
  }
  &[data-apagado="sim"] {
    opacity: 0.45;
  }

  /* Cartão compacto: título = setor, selo = alertas, sub = etapa do
     gerenciador, número à direita da sub. ~56 px por cartão. */
  @media ${mq.tablet} {
    min-height: 0;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "nome alertas"
      "etapa numero";
    gap: 1px 8px;
    padding: 6px 10px;
    border-radius: 9px;

    .nome {
      grid-area: nome;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13.5px;
      line-height: 1.3;
    }
    .alertas {
      grid-area: alertas;
      flex-wrap: nowrap;
      justify-content: flex-end;
      min-width: 0;
      max-width: 60vw;
      overflow: hidden;
    }
    .etapa {
      grid-area: etapa;
      font-size: 12.5px;
      line-height: 1.3;
    }
    .numero {
      grid-area: numero;
      grid-row: auto;
      justify-self: end;
      font-size: 16px;
      line-height: 1.2;
    }
    .calmo {
      font-size: var(--text-min, 12px);
    }
    &[data-tom="danger"] {
      border-color: var(--border);
      box-shadow: inset 3px 0 0 var(--danger-text);
    }
  }
`;

// ---- Listas de demandas (bandejas e resultados da busca) ----

export const Bloco = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & + & {
    margin-top: 20px;
  }
`;

export const TituloBloco = styled.h3`
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  color: var(--ink-2);

  /* "Criar setor para <etapa>": o atalho fica no título do grupo, ao lado da
     etapa que não tem sala — é ali que a falta aparece. */
  .acao {
    min-height: 32px;
    ${alvoNoToque}
    margin-left: auto;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--ink-2);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    cursor: pointer;
    ${foco}
    &:hover {
      border-color: var(--accent);
      color: var(--ink);
    }
  }
`;

export const Contador = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  min-height: 20px;
  border: 1px solid;
  border-radius: var(--radius-pill);
  font-size: var(--text-sm);
  ${({ $tom }) => TONS[$tom] || TONS.neutral}
`;

export const Cartao = styled.button`
  width: 100%;
  min-height: 48px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  ${foco}

  &:hover {
    background: var(--surface-2);
  }

  .titulo {
    font-weight: var(--weight-bold);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .linha {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    font-size: var(--text-md);
    color: var(--ink-2);
    min-width: 0;
  }
  .prazo-atrasado {
    color: var(--danger-text);
    font-weight: var(--weight-bold);
  }
`;

export const Selos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  min-height: 22px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: var(--radius-pill);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  white-space: nowrap;
  ${({ $tom }) => TONS[$tom] || TONS.neutral}
`;

export const Resultados = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ---- Formulário do setor (novo / editar) ----

/*
 * Três campos, nesta ordem: nome, etapa, recepção. O desenho já foi feito com o
 * dedo; pedir um formulário longo depois disso é o jeito mais rápido de o
 * gestor desistir no meio.
 */
export const FormularioDoSetor = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const CampoDoSetor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  /* Rótulo ACIMA do campo, nunca placeholder no lugar de rótulo. */
  > label,
  .rotulo {
    font-size: var(--text-md);
    font-weight: var(--weight-semibold);
    color: var(--ink-2);
  }
  input[type="text"],
  select {
    width: 100%;
    min-height: ${ALVO_TOQUE};
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    ${foco}
  }
  select {
    /* Seta nativa some no Safari sem isto; a lista de etapas é longa e o
       gestor precisa ver que o campo abre. */
    appearance: auto;
  }
  input[aria-invalid="true"],
  select[aria-invalid="true"] {
    border-color: var(--danger-border);
  }
  .ajuda {
    margin: 0;
    font-size: var(--text-md);
    color: var(--ink-3);
    text-wrap: pretty;
  }
  /* Erro embaixo do campo, ligado por aria-describedby (ver FormularioSetor). */
  .erro {
    margin: 0;
    font-size: var(--text-md);
    font-weight: var(--weight-semibold);
    color: var(--danger-text);
  }

  &[data-tipo="marcavel"] > label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-height: ${ALVO_TOQUE};
    font-weight: var(--weight-regular);
    color: var(--ink);
    cursor: pointer;
  }
  &[data-tipo="marcavel"] input {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    flex: none;
    accent-color: var(--accent);
    cursor: pointer;
    ${foco}
  }

  &[data-tipo="perigo"] {
    margin-top: 4px;
    padding-top: 16px;
    border-top: 1px solid var(--divider);
  }
`;

export const RodapeDoSetor = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const AvisoForm = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--danger-border);
  border-radius: var(--radius-md);
  background: var(--danger-bg);
  color: var(--danger-text);
  font-size: var(--text-md);

  svg {
    flex: none;
    width: 16px;
    height: 16px;
    margin-top: 2px;
  }
`;

/* "Apontamentos do dia" (ExportMenu: div > button) vira só ícone de 36 px no
   celular/tablet. O texto continua no DOM — é o nome acessível do botão. */
export const ApontamentosSoIcone = styled.span`
  display: contents;

  @media ${mq.tablet} {
    > div > button {
      width: 36px;
      min-width: 36px;
      min-height: 36px;
      padding: 0;
      gap: 0;
      font-size: 0;
      justify-content: center;
    }
    > div > button svg:last-child {
      display: none;
    }
    > div > button svg:first-child {
      width: 16px;
      height: 16px;
    }
  }
`;
